import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, Scale, Lock, Truck, CheckCircle } from 'lucide-react';

const advantages = [
  {
    icon: Shield,
    title: "Exceptional Strength",
    description: "Enhanced service life and long-term durability"
  },
  {
    icon: Zap,
    title: "Non-Breakable",
    description: "Significantly reducing accident risks"
  },
  {
    icon: Scale,
    title: "High Strength-to-Weight",
    description: "Saving up to 50% weight over traditional grey iron"
  },
  {
    icon: Lock,
    title: "Secure Designs",
    description: "Hinged designs that minimise theft"
  },
  {
    icon: Truck,
    title: "Heavy Traffic Ready",
    description: "Suitable for high-speed load conditions"
  },
  {
    icon: CheckCircle,
    title: "Anti-Skid Surface",
    description: "Attractive checker patterns for improved safety"
  }
];

export default function FeaturesSection() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#b58900] font-medium tracking-wider uppercase text-sm">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-light text-[#1a237e] mt-4 mb-6 leading-tight">
              EN 124 Compliant 
              <span className="font-medium block">Ductile Iron Products</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-8">
              Our products are engineered with cutting-edge technology and manufactured to the highest 
              European standards, delivering superior performance and value for infrastructure projects.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {advantages.map((adv, index) => (
                <motion.div
                  key={adv.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-8 h-8 bg-[#f9d71c]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <adv.icon className="w-4 h-4 text-[#1a237e]" />
                  </div>
                  <div>
                    <h4 className="text-[#1a237e] font-medium text-sm">{adv.title}</h4>
                    <p className="text-gray-500 text-xs mt-0.5">{adv.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative aspect-square rounded-3xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a237e] to-[#3949ab]" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-24 h-24 mx-auto mb-6 border-4 border-[#f9d71c] rounded-full flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">EN</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-2">124</h3>
                  <p className="text-white/70">European Standard Compliant</p>
                  <div className="mt-8 grid grid-cols-2 gap-4 text-center">
                    <div className="bg-white/10 rounded-xl p-4">
                      <p className="text-2xl font-bold text-[#f9d71c]">50%</p>
                      <p className="text-white/60 text-xs mt-1">Weight Savings</p>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <p className="text-2xl font-bold text-[#f9d71c]">D400</p>
                      <p className="text-white/60 text-xs mt-1">Load Class</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#f9d71c]/20 rounded-2xl -z-10" />
            <div className="absolute -top-6 -left-6 w-24 h-24 border-2 border-[#1a237e]/20 rounded-2xl -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}